import React from 'react'

import Script from 'dangerous-html/react'
import { Helmet } from 'react-helmet'

import Navigation from '../components/navigation'
import Footer from '../components/footer'
import './incident-response-management.css'

const IncidentResponseManagement = (props) => {
  return (
    <div className="irm-container1">
      <Helmet>
        <title>Cysigil Network Pvt Ltd</title>
        <meta
          property="og:title"
          content="Incident Response & Management - Cysigil"
        />
        <link
          rel="canonical"
          href="https://cysigil.com/incident-response-management"
        />
      </Helmet>

      <Navigation />

      {/* HERO */}
      <section className="hero-irm">
        <video
          src="/external/incident-response.mp4"
          loop
          muted
          autoPlay
          playsInline
          className="hero-irm-video"
        />
        <div className="hero-irm-overlay"></div>
        <div className="hero-irm-content">
          <div className="hero-irm-text">
            <h1 className="hero-title">
              Incident Response & Management
            </h1>
            <p className="hero-subtitle">
              Structured, rapid, and compliant response to cybersecurity
              incidents. Detect, contain, eradicate, and recover while meeting
              regulatory, customer, and board-level expectations.
            </p>
            <div className="hero-irm-actions">
              <a href="#contact">
                <div className="btn-primary btn btn-lg">
                  <span>Activate IR Support</span>
                </div>
              </a>
              <a href="#contact">
                <div className="btn btn-lg btn-outline">
                  <span style={{ color: 'black' }}>Talk to Expert</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="features-irm">
        <div className="features-irm-container">
          <div className="features-irm-header">
            <h2 className="section-title">
              Incident Response Coverage
            </h2>
            <p className="section-subtitle">
              End-to-end incident management across people, process, and technology
            </p>
          </div>

          <div className="features-irm-grid">
            <div className="features-irm-item">
              <h3 className="features-irm-title">Incident Detection & Analysis</h3>
              <p className="section-content">
                Identification, validation, and severity classification of
                security incidents using SIEM, EDR, cloud logs, and alerts.
              </p>
            </div>

            <div className="features-irm-item">
              <h3 className="features-irm-title">Containment & Isolation</h3>
              <p className="section-content">
                Immediate containment actions to stop attacker activity,
                prevent lateral movement, and limit business impact.
              </p>
            </div>

            <div className="features-irm-item">
              <h3 className="features-irm-title">Eradication</h3>
              <p className="section-content">
                Removal of malware, persistence mechanisms, compromised
                credentials, and vulnerable configurations.
              </p>
            </div>

            <div className="features-irm-item">
              <h3 className="features-irm-title">Recovery & Restoration</h3>
              <p className="section-content">
                Secure restoration of systems and services with validation to
                ensure threats are fully eliminated.
              </p>
            </div>

            <div className="features-irm-item">
              <h3 className="features-irm-title">Crisis Communication</h3>
              <p className="section-content">
                Support for executive, customer, regulator, and stakeholder
                communications during major incidents.
              </p>
            </div>

            <div className="features-irm-item">
              <h3 className="features-irm-title">Post-Incident Review</h3>
              <p className="section-content">
                Root cause analysis, lessons learned, and improvement roadmap
                to strengthen security posture.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="process-irm">
        <div className="process-irm-container">
          <div className="process-irm-header">
            <h2 className="section-title">
              Incident Response Lifecycle
            </h2>
            <p className="section-subtitle">
              Aligned with NIST, ISO 27035, and CERT-In expectations
            </p>
          </div>

          <div className="process-irm-steps">
            <div className="process-irm-cards-row">
              <div className="process-irm-card animate-card">
                <span className="process-irm-card-number">01</span>
                <h3 className="process-irm-card-title">Preparation</h3>
                <p className="section-content">
                  IR plans, roles, playbooks, escalation paths, and tooling readiness.
                </p>
              </div>
              <div className="process-irm-card animate-card">
                <span className="process-irm-card-number">02</span>
                <h3 className="process-irm-card-title">Detection & Analysis</h3>
                <p className="section-content">
                  Incident identification, validation, severity assessment, and triage.
                </p>
              </div>
              <div className="process-irm-card animate-card">
                <span className="process-irm-card-number">03</span>
                <h3 className="process-irm-card-title">Containment & Eradication</h3>
                <p className="section-content">
                  Short-term and long-term containment followed by threat removal.
                </p>
              </div>
              <div className="process-irm-card animate-card">
                <span className="process-irm-card-number">04</span>
                <h3 className="process-irm-card-title">Recovery & Lessons Learned</h3>
                <p className="section-content">
                  System recovery, monitoring, and post-incident improvements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="compliance-irm">
        <div className="compliance-irm-container">
          <h2 className="section-title">Compliance & Regulatory Alignment</h2>

          <table className="compliance-irm-table">
            <thead>
              <tr>
                <th>Framework / Regulation</th>
                <th>IR Coverage</th>
                <th>Support</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>ISO 27001 / 27035</td>
                <td>Incident handling & response management</td>
                <td>Audit Ready</td>
              </tr>
              <tr>
                <td>SOC 2 Type II</td>
                <td>Security incident response effectiveness</td>
                <td>Evidence Support</td>
              </tr>
              <tr>
                <td>DPDP / GDPR</td>
                <td>Breach notification & response</td>
                <td>Regulatory Defense</td>
              </tr>
              <tr>
                <td>CERT-In</td>
                <td>Mandatory incident reporting</td>
                <td>India Compliance</td>
              </tr>
              <tr>
                <td>NIST IR</td>
                <td>Incident response lifecycle</td>
                <td>Best Practice</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-irm">
        <h2 className="section-title">
          Be Ready Before the Next Incident
        </h2>
        <p className="section-subtitle">
          Partner with Cysigil to respond decisively, limit impact, and
          demonstrate control during security incidents.
        </p>
        <a href="#contact">
          <div className="btn-primary btn btn-lg">
            <span>Engage Incident Response Team</span>
          </div>
        </a>
      </section>

      {/* ANIMATIONS */}
      <Script
        html={`<script defer>
(function(){
  const items = document.querySelectorAll(
    ".features-irm-item, .process-irm-step"
  )
  const observer = new IntersectionObserver((entries)=>{
    entries.forEach(e=>{
      if(e.isIntersecting){
        e.target.style.opacity="1"
        e.target.style.transform="translateY(0)"
      }
    })
  },{threshold:0.15})

  items.forEach((el,i)=>{
    el.style.opacity="0"
    el.style.transform="translateY(20px)"
    el.style.transition=\0.5s ease \${i*0.1}s\
    observer.observe(el)
  })
})()
</script>`}
      />

      <Footer />
    </div>
  )
}

export default IncidentResponseManagement